import * as THREE from 'three';
import { settings } from '@/config';
import { toVector3 } from '@/utils/math';

/**
 * Minimal validation lighting: one sun (directional + PCF soft shadows), one
 * ambient fill, and an optional hemisphere. Deliberately un-artistic — just
 * enough to read baked textures and verify the shadow pipeline.
 */
export class Lights {
  readonly group = new THREE.Group();
  readonly ambient: THREE.AmbientLight;
  readonly sun: THREE.DirectionalLight;
  readonly hemisphere: THREE.HemisphereLight;

  constructor() {
    const cfg = settings.lights;

    this.ambient = new THREE.AmbientLight(new THREE.Color(cfg.ambient.color), cfg.ambient.intensity);

    this.sun = new THREE.DirectionalLight(new THREE.Color(cfg.sun.color), cfg.sun.intensity);
    this.sun.position.copy(toVector3(cfg.sun.position));
    this.sun.castShadow = cfg.sun.castShadow;
    this.configureShadow();

    this.hemisphere = new THREE.HemisphereLight(
      new THREE.Color(cfg.hemisphere.skyColor),
      new THREE.Color(cfg.hemisphere.groundColor),
      cfg.hemisphere.intensity,
    );
    this.hemisphere.visible = cfg.hemisphere.enabled;

    this.group.name = 'lights';
    this.group.add(this.ambient, this.sun, this.sun.target, this.hemisphere);
  }

  private configureShadow(): void {
    const s = settings.lights.sun.shadow;
    const cam = this.sun.shadow.camera;
    this.sun.shadow.mapSize.set(s.mapSize, s.mapSize);
    cam.near = s.near;
    cam.far = s.far;
    cam.left = -s.bounds;
    cam.right = s.bounds;
    cam.top = s.bounds;
    cam.bottom = -s.bounds;
    cam.updateProjectionMatrix();
    this.sun.shadow.bias = s.bias;
    this.sun.shadow.normalBias = s.normalBias;
    this.sun.shadow.radius = s.radius;
  }

  setShadowsEnabled(enabled: boolean): void {
    this.sun.castShadow = enabled;
  }

  dispose(): void {
    this.sun.shadow.map?.dispose();
    this.group.clear();
  }
}
